import { AlertTriangle, CheckCircle2, Info, X, XCircle } from "lucide-react";
import type { ReactNode } from "react";
import { Spinner } from "@/shared/ui/spinner";
import { cn } from "@/shared/lib/utils";
import { useAsyncFeedbackStore, type ToastKind } from "./asyncFeedbackStore";

const toastIcon: Record<ToastKind, typeof CheckCircle2> = {
  success: CheckCircle2,
  error: XCircle,
  info: Info,
  warning: AlertTriangle,
};

/** Superfície neutra + faixa/ícone semântico (calmo; legível nos dois temas). */
const toastTone: Record<ToastKind, { bar: string; icon: string }> = {
  success: { bar: "bg-success", icon: "text-success-fg" },
  error: { bar: "bg-danger", icon: "text-danger-fg" },
  info: { bar: "bg-info", icon: "text-info-fg" },
  warning: { bar: "bg-warning", icon: "text-warning-fg" },
};

export function AsyncFeedbackProvider({ children }: { children: ReactNode }) {
  const httpPending = useAsyncFeedbackStore((s) => s.httpPending);
  const operationLabel = useAsyncFeedbackStore((s) => s.operationLabel);
  const toasts = useAsyncFeedbackStore((s) => s.toasts);
  const dismissToast = useAsyncFeedbackStore((s) => s.dismissToast);

  const active = httpPending > 0 || operationLabel != null;

  return (
    <>
      <div
        className={cn(
          "pm-global-progress pointer-events-none fixed inset-x-0 top-0 z-[200] transition-opacity duration-300",
          active ? "opacity-100" : "opacity-0",
        )}
        aria-hidden
      >
        <div className="pm-global-progress__bar" />
      </div>

      {/* Só operações nomeadas (salvar, excluir…) ganham pílula; leituras de fundo ficam só na barra. */}
      {operationLabel ? (
        <div
          className="pointer-events-none fixed left-1/2 top-3 z-[201] flex -translate-x-1/2 items-center gap-2 rounded-full border border-border bg-surface-raised px-3 py-1.5 text-body-sm font-medium text-fg shadow-md animate-pop-in"
          role="status"
          aria-live="polite"
        >
          <Spinner className="h-3.5 w-3.5 text-brand-accent" />
          {operationLabel}
        </div>
      ) : null}

      <div
        className="pointer-events-none fixed bottom-4 right-4 z-[202] flex w-[min(calc(100vw-2rem),22rem)] flex-col gap-2"
        aria-live="polite"
        aria-relevant="additions"
      >
        {toasts.map((t) => {
          const Icon = toastIcon[t.kind];
          const tone = toastTone[t.kind];
          return (
            <div
              key={t.id}
              className="pm-toast-enter pointer-events-auto relative flex items-start gap-3 overflow-hidden rounded-lg border border-border bg-surface-raised py-3 pl-4 pr-2 text-fg shadow-md"
              role={t.kind === "error" ? "alert" : "status"}
            >
              <span className={cn("absolute inset-y-0 left-0 w-1", tone.bar)} aria-hidden />
              <Icon className={cn("mt-0.5 h-4 w-4 shrink-0", tone.icon)} aria-hidden />
              <p className="flex-1 text-body font-medium">{t.message}</p>
              <button
                type="button"
                className="rounded-md p-1 text-fg-subtle transition-colors hover:bg-surface-hover hover:text-fg"
                onClick={() => dismissToast(t.id)}
                aria-label="Fechar notificação"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          );
        })}
      </div>

      {children}
    </>
  );
}
